import React from 'react'
import { ContentAsset } from '../../swagger'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FileType } from '../Elements/FileTypeResource'
import LearningCenterContentCard from '../Card/LearningCenterContentCard'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import EmptyLearningCenter from './EmptyLearningCenter'
import {
  buildLanguageFilter,
  isFeaturedLearningPathAsset,
} from './utils/learningCenterUtils'
import { useLearningCenterContext } from '../Context/LearningCenterContext'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import { formatLanguage } from '../../utils/languageUtility'
import { LearningCenterTabs } from '../../utils/searchAndFilterEnums'

interface WelcomeCenterTabProps {
  content: ContentAsset[]
}

export const WelcomeCenterTab: React.FC<WelcomeCenterTabProps> = ({
  content,
}) => {
  const {
    updateLearningCenterAssetById,
    learningCenterOptions,
    language,
    tabSpecificSearchAndFilter,
    updateTabSpecificSearchAndFilter,
  } = useLearningCenterContext()
  const { t } = useTranslation()
  const accordionSectionTitle = t(
    'LearningCenter.WelcomeTab.Heading',
    'Welcome To CC Connected'
  )
  const theme = useTheme()
  const showOnDesktop = useShowOnDesktop()

  const { showLanguageFilter, languageOptions } = buildLanguageFilter(
    learningCenterOptions.languages
  )

  const handleLanguageSelection = (selection: string) => {
    updateTabSpecificSearchAndFilter({
      ...tabSpecificSearchAndFilter,
      [LearningCenterTabs.WelcomeCenter]: {
        ...tabSpecificSearchAndFilter[LearningCenterTabs.WelcomeCenter],
        language: selection,
      },
    })
  }

  return (
    <div
      style={{
        margin: theme.spacing(3),
        ...(showOnDesktop && {
          margin: theme.spacing(3, 2),
        }),
      }}
    >
      {/* Outside the content check so a selection that empties the list keeps the dropdown. */}
      {showLanguageFilter && (
        <Box mb={2}>
          <DropDown
            id="filterByLanguage"
            menuOptions={languageOptions}
            handleSelection={handleLanguageSelection}
            variant={DropDownVariant.SortAndFilter}
            value={language}
          />
        </Box>
      )}
      {content.length > 0 ? (
        <Accordion defaultExpanded TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="welcomeCenter-content"
            id="welcomeCenter-header"
          >
            <Typography variant="button" component="p">
              {accordionSectionTitle}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              ...(showOnDesktop && {
                justifyContent: 'left',
              }),
            }}
            aria-labelledby="welcomeCenter-header"
          >
            {content.map((details) => {
              const isFeaturedLearningCenterPathAsset =
                isFeaturedLearningPathAsset({
                  learningPathTitle: details.learningPathTitle,
                  listingPriority: details.listingPriority,
                })

              return (
                <LearningCenterContentCard
                  isNdaRequired={details.isNdaRequired}
                  ndaAcknowledged={details.ndaAcknowledged}
                  key={details.assetKey}
                  id={details.assetKey}
                  fileType={
                    isFeaturedLearningCenterPathAsset
                      ? FileType.LearningPath
                      : (details.filetype as FileType)
                  }
                  title={
                    isFeaturedLearningCenterPathAsset
                      ? (details.learningPathTitle as string)
                      : details.title ||
                        t('LearningCenter.EmptyTitle', 'No title available')
                  }
                  description={
                    isFeaturedLearningCenterPathAsset &&
                    !!details.learningPathDescription
                      ? details.learningPathDescription
                      : details.description ||
                        t(
                          'LearningCenter.EmptyDescription',
                          'No description available'
                        )
                  }
                  subTitle={accordionSectionTitle}
                  strand={details.strand ?? ''}
                  isFavorite={details.isFavorited ?? false}
                  viewOnly={details.viewOnly ?? false}
                  isLearningPath={isFeaturedLearningCenterPathAsset}
                  language={
                    isFeaturedLearningCenterPathAsset
                      ? undefined
                      : formatLanguage(details.language)
                  }
                  handleFavoriteButtonClicked={() =>
                    updateLearningCenterAssetById(details.assetKey, {
                      isFavorited: !details.isFavorited,
                    })
                  }
                />
              )
            })}
          </AccordionDetails>
        </Accordion>
      ) : (
        <EmptyLearningCenter />
      )}
    </div>
  )
}

export default WelcomeCenterTab
